import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** iter-signup-notify · Oct-2026.
 *
 *  Recipient inbox for a one-line email sent whenever a new user signs
 *  up (see `routes_auth.py::trial_signup`). Separate from the admin
 *  recovery email so you can route it to a marketing/ops inbox instead.
 */
export default function SignupNotificationEmailConfig({ cfg, onSaved }) {
  const [email, setEmail] = useState(cfg?.signup_notification_email || '');
  const [busy, setBusy]   = useState(false);
  useEffect(() => { setEmail(cfg?.signup_notification_email || ''); }, [cfg?.signup_notification_email]);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ signup_notification_email: email.trim() });
      toast.success(email ? 'Signup notification email saved.' : 'Signup notifications turned off.');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setBusy(false); }
  };

  const isConfigured = Boolean(cfg?.signup_notification_email);

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="signup-notification-email">
      <div className="flex items-start gap-2">
        <UserPlus className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">New Signup Notifications</div>
          <div className="text-[11px] text-muted-foreground">
            Get a short email every time a new account signs up (name, email, and which
            channel/campaign they came from, if known).
            {' '}
            {isConfigured
              ? <span className="text-success">Currently configured.</span>
              : <span className="text-warning-foreground">Not configured — no signup emails are sent.</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <div className="flex-1 max-w-md">
          <Label className="text-[11px]">Notify email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourdomain.com"
            className="mt-1 h-8 text-[11.5px]"
            data-testid="signup-notification-email-input"
          />
        </div>
        <div className="flex items-end">
          <Button
            size="sm"
            onClick={save}
            disabled={busy}
            className="h-8 gap-1.5"
            data-testid="signup-notification-email-save"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
